import React, { useState, useEffect, useMemo } from "react";
import {
  BookOpen,
  Users,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  Zap,
  BarChart3,
  FolderOpen,
  Mail,
  Settings,
  HelpCircle,
  IdCard,
  ChevronDown,
  ChevronRight,
  LogOut,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  Calendar,
  History,
} from "lucide-react";
import menuItems from "../data/menuItems.json";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/actions/authActions";
import { selectAccess, selectModuleGrants } from "../redux/selectors/authSelectors";
import { canOpenMenuItem, hasDestination, visibleMenuEntry } from "../utils/landingRoute";
import Logo from "./icons/Logo";

interface MenuItem {
  start: any;
  divided: any;
  id: string;
  name: string;
  icon: string;
  path: string;
  active?: boolean;
  hasDropdown?: boolean;
  subItems?: { id: string; name: string; path: string }[];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const iconMap = {
  BookOpen,
  Users,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  Zap,
  BarChart3,
  FolderOpen,
  Mail,
  Settings,
  HelpCircle,
  Calendar,
  IdCard,
  History,
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  // Nothing is highlighted until the effect below matches the current path.
  // This used to seed "dashboard", which highlighted an entry that no longer
  // exists; the effect sets the right one on the first render anyway.
  const [activeItem, setActiveItem] = useState("");
  const [activeSubItem, setActiveSubItem] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // One object rather than a selector per flag: the same map decides where the
  // user lands after login, so both read it from utils/landingRoute and cannot
  // disagree about which entries an account can open.
  const access = useSelector(selectAccess);
  const grants = useSelector(selectModuleGrants);

  // Filter menu items based on access — and, when Settings → Authorization has
  // a grid for this account, on that grid down to the sub-items.
  const filteredMenuItems = useMemo(
    () =>
      (menuItems as MenuItem[])
        .filter((item) => canOpenMenuItem(item.id, access, grants))
        .map((item) => visibleMenuEntry(item, grants))
        .filter(hasDestination),
    [access, grants]
  );

  // Determine active item based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check all filtered menu items and their subitems
    filteredMenuItems.forEach((item) => {
      // Check if current path matches the item's path
      if (item.path === currentPath) {
        setActiveItem(item.id);
        setActiveSubItem(null);
        return;
      }
      
      // Check subitems
      if (item.subItems) {
        item.subItems.forEach((sub) => {
          if (sub.path === currentPath) {
            setActiveSubItem(sub.id);
            // When collapsed, highlight parent; when expanded, highlight subitem
            setActiveItem(isCollapsed ? item.id : sub.id);
            // Auto-expand the parent if it has a dropdown
            if (item.hasDropdown) {
              setExpandedItems((prev) => {
                if (!prev.includes(item.id)) {
                  return [...prev, item.id];
                }
                return prev;
              });
            }
            return;
          }
        });
      }
    });
  }, [location.pathname, isCollapsed, filteredMenuItems]);

  const toggleExpanded = (id: string) =>
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const handleItemClick = (id: string, path: string, hasDropdown?: boolean, subItems?: { id: string; name: string; path: string }[]) => {
    if (hasDropdown && subItems && isCollapsed) {
      const firstSubItem = subItems[0];
      navigate(firstSubItem.path);
      setActiveItem(id); // When collapsed, highlight parent
      setActiveSubItem(firstSubItem.id);
      return;
    }
    setActiveItem(id);
    setActiveSubItem(null);
    if (hasDropdown) {
      toggleExpanded(id);
      return;
    }
    // Navigate to the item's path if it doesn't have a dropdown
    navigate(path);
  };

  const renderIcon = (iconName: string) => {
    const Icon = iconMap[iconName as keyof typeof iconMap];
    return Icon ? <Icon className="h-5 w-5" /> : null;
  };

  return (
    <aside
      className={[
        /*
         * h-full, not h-dvh. The shell is a flex row with a real height now,
         * so the sidebar stretches to it — and a second 100dvh here would
         * collapse to nothing in the same browsers the shell was fixed for,
         * leaving the nav a sliver beside a full-height page.
         */
        "flex h-full min-h-0 flex-col bg-gray-900 text-white transition-[width] duration-300",
        isCollapsed ? "w-16" : "w-64",
      ].join(" ")}
    >
      {/* Header (not scrollable) */}
      <div className="flex items-center justify-between px-3 py-3">
        {!isCollapsed && (
          <h1 className="flex items-center gap-2 text-xl font-bold">
            <Logo /> RemNutri
          </h1>
        )}
        <button
          onClick={onToggle}
          className="rounded-lg p-1 hover:bg-gray-700"
          aria-label="Toggle sidebar"
        >
          {isCollapsed ? <Logo /> : <ChevronRight className="h-5 w-5 rotate-180" />}
        </button>
      </div>

      {/* NAV (the ONLY scrollable region) */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-2 hide-scrollbar">
        <ul className="space-y-1 px-2">
          {filteredMenuItems.map((item) => (
            <li
              key={item.id}
              className={[
                item.divided ? "border-b border-white/10 pb-3" : "",
                item.start ? "pt-3" : "",
              ].join(" ")}
            >
              <button
                onClick={() => handleItemClick(item.id, item.path, item.hasDropdown, item?.subItems)}
                className={[
                  "group mb-2 flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition",
                  // When collapsed: highlight parent if any subitem is active
                  // When expanded: only highlight parent if no subitem is active (parent itself is active)
                  (isCollapsed && activeSubItem && item.subItems?.some(sub => sub.id === activeSubItem)) ||
                  (!isCollapsed && activeItem === item.id && !activeSubItem)
                    ? "bg-indigo-600 text-white"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white",
                ].join(" ")}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={[
                      "shrink-0",
                      (isCollapsed && activeSubItem && item.subItems?.some(sub => sub.id === activeSubItem)) ||
                      (!isCollapsed && activeItem === item.id && !activeSubItem)
                        ? "text-white"
                        : "text-gray-400 group-hover:text-white",
                    ].join(" ")}
                  >
                    {renderIcon(item.icon)}
                  </span>
                  {!isCollapsed && <span className="font-medium">{item.name}</span>}
                </div>
                {!isCollapsed && item.hasDropdown && (
                  <ChevronDown
                    className={[
                      "h-4 w-4 transition-transform",
                      expandedItems.includes(item.id) ? "rotate-180" : "",
                    ].join(" ")}
                  />
                )}
              </button>

              {!isCollapsed && item.hasDropdown && item.subItems && (
                <div
                  className={[
                    "overflow-hidden pl-9 transition-all",
                    expandedItems.includes(item.id)
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0",
                  ].join(" ")}
                >
                  <ul className="space-y-1">
                    {item.subItems.map((sub) => (
                      <li key={sub.id}>
                        <button
                          onClick={() => {
                            setActiveSubItem(sub.id);
                            setActiveItem(sub.id); // When expanded, highlight subitem
                            navigate(sub.path);
                          }}
                          className={[
                            "w-full rounded-lg px-3 py-2 text-left text-sm transition-colors",
                            activeSubItem === sub.id
                              ? "bg-indigo-500 text-white"
                              : "text-gray-400 hover:bg-gray-700 hover:text-white",
                          ].join(" ")}
                        >
                          {sub.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer (always visible / sticky at bottom) */}
      <div className="sticky bottom-0 z-10 border-t border-gray-800 bg-gray-900/95 px-3 py-3 backdrop-blur">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <button
              className="flex w-3/5 items-center justify-center gap-2 rounded-lg bg-red-700 px-3 py-2.5 text-sm font-medium text-gray-100 transition hover:bg-red-600"
              onClick={() => {
                dispatch(logout());
                navigate("/login");
              }}
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          )}
          <button
            onClick={onToggle}
            className="rounded-lg p-2 hover:bg-gray-700"
            aria-label="Collapse"
          >
            {isCollapsed ? (
              <ArrowLeftFromLine className="h-5 w-5 rotate-180" />
            ) : (
              <ArrowRightFromLine className="h-5 w-5 rotate-180" />
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
